import { Portal } from "../Portal/Portal";
import styles from "./DropOverMobile.module.less";
import {
	MouseEvent,
	MouseEventHandler,
	PropsWithChildren,
	ReactElement,
	RefObject,
	useEffect,
	useMemo,
	useRef,
} from "react";
import {
	DropOverContext,
	DropOverContextData,
	DropOverProps
} from "./DropOver";
import { Guid, newGuid } from "client_legacy/ndaw/guid";
import { useDropOverStore } from "./dropOverMobileStore";
import { useOnCurrentWindowBlur } from "./useOnCurrentWindowBlur";

export const DropOverMobile = (props: PropsWithChildren<DropOverProps>): ReactElement => {
	const {
		onClose,
		bubbleClick,
		bubbleContextMenu = true,
		children,
		ariaMultiselectable,
		id: htmlId,
		role,
		ariaLabel,
		onKeyUp,
		shouldCloseWhenWindowLoosesFocus = false
	} = props;

	const containerRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
	const container: HTMLElement = document.body;
	const id: Guid = useMemo(() => newGuid(), []);
	const { stack, add, remove } = useDropOverStore();
	const lastItem: Guid = stack[stack.length - 1];

	const backdropClickHandler: MouseEventHandler = (event) => {
		if (!containerRef.current) {
			return;
		}

		const path: EventTarget[] = event.nativeEvent.composedPath();
		const [targetElement] = path;

		if ((targetElement as HTMLElement).classList.contains(styles.root)) {
			onClose?.();
		}

		if (bubbleClick === false) {
			event.stopPropagation();
		}
	};

	useEffect(
		() => {
			add(id);

			return () => remove(id);
		},
		[id, add, remove]
	);

	useOnCurrentWindowBlur(
		shouldCloseWhenWindowLoosesFocus
			? onClose
			: undefined
	);

	const contentContext: DropOverContextData = useMemo(
		(): DropOverContextData => ({
			onClose,
		}),
		[onClose]
	);

	const contextMenuHandler = useMemo(
		(): ((args: MouseEvent) => void) | undefined => {
			if (bubbleContextMenu === false) {
				return (args: MouseEvent): void => {
					args.stopPropagation();
				};
			} else {
				return undefined;
			}
		},
		[bubbleContextMenu]
	);

	return (
		<Portal container={container}>
			<div
				className={styles.root}
				style={{ opacity: id === lastItem ? 1 : 0 }}
				onClick={backdropClickHandler}
				ref={containerRef}
				onContextMenu={contextMenuHandler}
				aria-multiselectable={ariaMultiselectable}
				id={htmlId}
				role={role}
				aria-label={ariaLabel}
				onKeyUp={onKeyUp}
			>
				<DropOverContext.Provider value={contentContext}>
					{children}
				</DropOverContext.Provider>
			</div>
		</Portal>
	);
};
